import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import t from "@/lang/lang";
import { LocationType } from "@/services/backend/buildings/types/location-type";
import {
  areaCountOfLevel,
  levelsOfType,
} from "@/services/backend/buildings/lib/building-areas";
import {
  useBuildingsLevelCreateMutation,
  useBuildingsLevelDeleteMutation,
  useBuildingsLevelUpdateMutation,
} from "@/services/backend/buildings/api/building-api";
import { type Building } from "@/services/backend/buildings/types/building";
import { type Level } from "@/services/backend/buildings/types/level";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";

/** Creates, renames and removes the levels of a building. */
export function LevelManagerDialogButton({
  building,
  scopeType,
}: {
  building: Building;
  scopeType?: LocationType;
}) {
  const levels = levelsOfType(building, scopeType);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-8">
          <Pencil className="h-4 w-4" />
          {t("Ebenen")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("Ebenen verwalten")}</DialogTitle>
          <DialogDescription>
            {t(
              "Hier können Ebenen angelegt, umbenannt und, sofern sie leer sind, gelöscht werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <CreateLevelRow
            buildingId={building.id}
            defaultType={scopeType ?? LocationType.LocationTypeInside}
          />
          {levels.length === 0 ? (
            <div className="text-muted-foreground text-sm">
              {t("Es sind noch keine Ebenen vorhanden.")}
            </div>
          ) : (
            levels.map((level) => (
              <LevelManagerRow
                key={level.id}
                buildingId={building.id}
                level={level}
                areaCount={areaCountOfLevel(building, level.id)}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateLevelRow({
  buildingId,
  defaultType,
}: {
  buildingId: string;
  defaultType: LocationType;
}) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [type, setType] = useState(defaultType);
  const [showValidation, setShowValidation] = useState(false);
  const [createLevel, createState] = useBuildingsLevelCreateMutation();

  useEffect(() => {
    if (createState.error) {
      const parsedError = parseRTKQueryError(createState.error);
      if (parsedError) {
        toast({ ...parsedError, variant: "destructive" });
      }
      createState.reset();
    }
  }, [createState, toast]);

  useEffect(() => {
    if (createState.isSuccess) {
      setName("");
      createState.reset();
    }
  }, [createState]);

  const handleCreate = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setShowValidation(true);
      return;
    }
    createLevel({ id: buildingId, levelId: v4(), name: trimmedName, type });
  };

  const nameError =
    showValidation && !name.trim()
      ? t("Der Ebenenname darf nicht leer sein.")
      : "";

  return (
    <div className="grid gap-2 rounded-md border border-dashed p-3">
      <div className="flex items-center gap-3">
        <div className="grid flex-1 gap-1">
          <Input
            value={name}
            placeholder={t("Neue Ebene")}
            onChange={(event) => {
              setName(event.target.value);
              if (showValidation && event.target.value.trim()) {
                setShowValidation(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleCreate();
              }
            }}
            aria-invalid={!!nameError}
          />
        </div>
        <Select
          value={type}
          onValueChange={(value) => setType(value as LocationType)}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder={t("Bereichstyp")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={LocationType.LocationTypeInside}>
              {t("Innenbereich")}
            </SelectItem>
            <SelectItem value={LocationType.LocationTypeOutside}>
              {t("Außenbereich")}
            </SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleCreate} disabled={createState.isLoading}>
          {createState.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {t("Ebene anlegen")}
        </Button>
      </div>
      {nameError ? (
        <div className="text-destructive text-sm">{nameError}</div>
      ) : null}
    </div>
  );
}

function LevelManagerRow({
  buildingId,
  level,
  areaCount,
}: {
  buildingId: string;
  level: Level;
  areaCount: number;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(level.name);
  const [editMode, setEditMode] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [saveLevel, saveState] = useBuildingsLevelUpdateMutation();
  const [deleteLevel, deleteState] = useBuildingsLevelDeleteMutation();

  useEffect(() => {
    setName(level.name);
    setShowValidation(false);
  }, [level.name]);

  useEffect(() => {
    if (saveState.error) {
      const parsedError = parseRTKQueryError(saveState.error);
      if (parsedError) {
        toast({ ...parsedError, variant: "destructive" });
      }
      saveState.reset();
    }
  }, [saveState, toast]);

  useEffect(() => {
    if (deleteState.error) {
      const parsedError = parseRTKQueryError(deleteState.error);
      if (parsedError) {
        toast({ ...parsedError, variant: "destructive" });
      }
      deleteState.reset();
    }
  }, [deleteState, toast]);

  useEffect(() => {
    if (saveState.isSuccess) {
      setEditMode(false);
      saveState.reset();
    }
  }, [saveState]);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setShowValidation(true);
      return;
    }
    saveLevel({
      id: buildingId,
      levelId: level.id,
      name: trimmedName,
      type: level.type,
    });
  };

  const nameError =
    showValidation && !name.trim()
      ? t("Der Ebenenname darf nicht leer sein.")
      : "";

  return (
    <div className="flex items-center gap-3 rounded-md border p-3">
      <div className="grid flex-1 gap-1">
        {editMode ? (
          <div className="grid gap-1">
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (showValidation && event.target.value.trim()) {
                  setShowValidation(false);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSave();
                }
              }}
              aria-invalid={!!nameError}
            />
            {nameError ? (
              <div className="text-destructive text-sm">{nameError}</div>
            ) : null}
          </div>
        ) : (
          <div className="font-medium">{level.name}</div>
        )}
        <div className="text-muted-foreground text-sm">
          {areaCount === 0
            ? t("Keine Bereiche zugeordnet")
            : t(`${areaCount} Bereiche zugeordnet`)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {editMode ? (
          <>
            <Button
              size="icon"
              onClick={handleSave}
              disabled={saveState.isLoading}
            >
              {saveState.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => {
                setName(level.name);
                setShowValidation(false);
                setEditMode(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button
            size="icon"
            variant="outline"
            onClick={() => setEditMode(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="icon"
              variant="destructive"
              disabled={areaCount > 0 || deleteState.isLoading}
            >
              {deleteState.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("Ebene löschen")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("Diese leere Ebene wird dauerhaft gelöscht.")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("Abbrechen")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  deleteLevel({ id: buildingId, levelId: level.id })
                }
              >
                {t("Löschen")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
